import React, { useState, useEffect } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { 
  ShoppingCart, 
  Clock, 
  CheckCircle2, 
  XCircle, 
  User, 
  CreditCard,
  AlertCircle,
  Package,
  DollarSign,
  Copy,
  Eye,
  RefreshCw,
  Filter,
  Calendar,
  Hash,
  Receipt,
  AlertTriangle
} from 'lucide-react';

import { useSettings } from '../../hooks/useSettings';

const OrderManagementTab = () => {
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total_orders: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false
  });
  const [filters, setFilters] = useState({
    status_filter: 'all',
    start_date: '',
    end_date: ''
  });
  const [summary, setSummary] = useState({
    total_orders: 0,
    total_revenue: 0,
    pending: 0,
    paid: 0,
    assigned: 0,
    failed: 0,
    cancelled: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assigningOrderId, setAssigningOrderId] = useState(null);
  const [showFixSummaryModal, setShowFixSummaryModal] = useState(false);
  const [fixSummary, setFixSummary] = useState(null);
  const [copySuccess, setCopySuccess] = useState('');
  const [syncingOrderId, setSyncingOrderId] = useState(null);
  const [syncMessage, setSyncMessage] = useState('');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';
  const { safeSettings } = useSettings();
  const displayTz = safeSettings?.timezone || 'UTC';

  // Initialize default date range (1st of current month to today)
  useEffect(() => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);

    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const defaultStart = formatDate(firstDayOfMonth);
    const defaultEnd = formatDate(today);
    setStartDate(defaultStart);
    setEndDate(defaultEnd);
    // Initial load with the default range. Fetch is decoupled from date-input state,
    // so the initial fetch is triggered explicitly here rather than by a date effect.
    fetchOrders({ startDate: defaultStart, endDate: defaultEnd, page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch orders from API with pagination and filters
  // Accepts optional overrides so callers (quick-date buttons, month stepper, initial
  // load) can fetch with values that haven't been committed to state yet — this avoids
  // the stale-closure problem and lets us decouple fetching from the date-input state.
  const fetchOrders = async (overrides = {}) => {
    const sf = overrides.statusFilter !== undefined ? overrides.statusFilter : statusFilter;
    const sd = overrides.startDate !== undefined ? overrides.startDate : startDate;
    const ed = overrides.endDate !== undefined ? overrides.endDate : endDate;
    const p = overrides.page !== undefined ? overrides.page : page;
    const ps = overrides.pageSize !== undefined ? overrides.pageSize : pageSize;
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');

      // Build query parameters
      const params = new URLSearchParams();
      if (sf && sf !== 'all') {
        params.append('status_filter', sf);
      }
      params.append('page', p);
      params.append('page_size', ps);
      if (sd) {
        params.append('start_date', sd);
      }
      if (ed) {
        params.append('end_date', ed);
      }

      const response = await fetch(
        `${API_BASE_URL}/api/admin/orders?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      // Handle new paginated response structure
      setOrders(data.orders || []);
      setPagination(data.pagination || {});
      setFilters(data.filters || {});
      setSummary(data.summary || {
        total_orders: 0,
        total_revenue: 0,
        pending: 0,
        paid: 0,
        assigned: 0,
        failed: 0,
        cancelled: 0
      });
      setError(null);
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  // Badge mapping for assigned_by values
  const badgeForAssignedBy = (value) => {
    const v = (value || '').toLowerCase();
    const map = [
      {
        match: (s) => s === 'admin_manual',
        label: 'Manual (Admin)',
        bg: '#eef2ff', border: '#c7d2fe', fg: '#3730a3'
      },
      {
        match: (s) => s === 'admin_fix',
        label: 'Fixed (Admin)',
        bg: '#fef3c7', border: '#fde68a', fg: '#92400e'
      },
      {
        match: (s) => s === 'reconcile_worker',
        label: 'Reconciled (Auto)',
        bg: '#ecfeff', border: '#a5f3fc', fg: '#0e7490'
      },
      {
        match: (s) => s === 'razorpay_webhook',
        label: 'Razorpay Webhook',
        bg: '#dcfce7', border: '#bbf7d0', fg: '#065f46'
      },
      {
        match: (s) => s === 'payment_recheck',
        label: 'Payment Recheck',
        bg: '#dbeafe', border: '#bfdbfe', fg: '#1e40af'
      },
      {
        match: (s) => s === 'system_auto',
        label: 'System (Auto)',
        bg: '#f3f4f6', border: '#e5e7eb', fg: '#374151'
      },
      {
        match: (s) => s.startsWith('admin_reconcile'),
        label: 'Reconciled (Admin)',
        bg: '#e0f2fe', border: '#bae6fd', fg: '#075985'
      }
    ];
    const found = map.find(m => m.match(v));
    return found || { label: value || 'Assigned', bg: '#f3f4f6', border: '#e5e7eb', fg: '#374151' };
  };

  // Assign plan from order
  const handleAssignOrder = async (orderId) => {
    try {
      if (!window.confirm('Are you sure you want to assign this plan? This will cancel any existing active subscription and create a new one.')) {
        return;
      }
      setAssigning(true);
      setAssigningOrderId(orderId);
      const adminToken = localStorage.getItem('admin_token');
      
      const response = await fetch(
        `${API_BASE_URL}/api/admin/orders/${orderId}/assign`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to assign plan');
      }

      // Refresh orders list
      await fetchOrders();
      setShowAssignModal(false);
      setSelectedOrder(null);
      
      // Show success message
      setTimeout(() => {
        setAssigningOrderId(null);
      }, 500);
      alert('Plan assigned successfully!');
      
    } catch (err) {
      console.error('Error assigning plan:', err);
      alert('Error assigning plan. Please try again.');
    } finally {
      setAssigning(false);
      setAssigningOrderId(null);
    }
  };

  // Copy to clipboard function
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(''), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Sync order status from payment gateway
  const handleSyncOrderStatus = async (orderId) => {
    try {
      setSyncingOrderId(orderId);
      const adminToken = localStorage.getItem('admin_token');

      const response = await fetch(
        `${API_BASE_URL}/api/admin/orders/${orderId}/sync-status`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        // Show success message
        setSyncMessage(result.message || 'Status synced successfully');
        setTimeout(() => setSyncMessage(''), 4000);

        // Refresh orders list to show updated status
        await fetchOrders();
      } else {
        alert(result.message || result.detail || 'Failed to sync order status');
      }

    } catch (err) {
      console.error('Error syncing order status:', err);
      alert('Error syncing order status. Please try again.');
    } finally {
      setSyncingOrderId(null);
    }
  };

  // Fix inconsistent orders
  const handleFixInconsistentOrders = async () => {
    if (!window.confirm('This will scan and fix inconsistent orders (mark paid and assign). Continue?')) {
      return null;
    }
    
    try {
      const adminToken = localStorage.getItem('admin_token');
      
      const response = await fetch(
        `${API_BASE_URL}/api/orders/fix-inconsistent`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        // Refresh orders list
        await fetchOrders();
        return result;
      } else {
        throw new Error('Failed to fix inconsistent orders');
      }
    } catch (err) {
      console.error('Error fixing orders:', err);
      alert('Error fixing inconsistent orders. Please try again.');
      return null;
    }
  };

  // Format a Date as YYYY-MM-DD for the date inputs / API
  const toInputDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Set a date range, reset to page 1, and fetch immediately. Dates are passed
  // explicitly to fetchOrders because setState is async — reading them back from
  // state here would be stale.
  const applyDateRange = (sd, ed) => {
    setStartDate(sd);
    setEndDate(ed);
    setPage(1);
    fetchOrders({ startDate: sd, endDate: ed, page: 1 });
  };

  // Quick date filter functions
  const setDateFilter = (type) => {
    const today = new Date();
    switch (type) {
      case 'today':
        applyDateRange(toInputDate(today), toInputDate(today));
        break;
      case 'last7days': {
        const last7 = new Date(today);
        last7.setDate(today.getDate() - 7);
        applyDateRange(toInputDate(last7), toInputDate(today));
        break;
      }
      case 'thisMonth': {
        const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
        applyDateRange(toInputDate(firstDay), toInputDate(today));
        break;
      }
      case 'lastMonth': {
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        applyDateRange(toInputDate(lastMonthStart), toInputDate(lastMonthEnd));
        break;
      }
      case 'ytd': {
        const jan1 = new Date(today.getFullYear(), 0, 1);
        applyDateRange(toInputDate(jan1), toInputDate(today));
        break;
      }
      default:
        break;
    }
  };

  // Step the viewed range by whole months relative to the CURRENT start date.
  // e.g. viewing May 2026 -> stepMonth(-1) sets 1–30 Apr 2026; again -> 1–31 Mar 2026.
  // Day 0 of the next month = last day of the target month (handles 28/29/30/31 + year rollover).
  const stepMonth = (delta) => {
    const base = startDate ? new Date(startDate + 'T00:00:00') : new Date();
    const firstOfMonth = new Date(base.getFullYear(), base.getMonth() + delta, 1);
    const lastOfMonth = new Date(base.getFullYear(), base.getMonth() + delta + 1, 0);
    applyDateRange(toInputDate(firstOfMonth), toInputDate(lastOfMonth));
  };

  // Handle filter changes - reset to page 1
  const handleFilterChange = () => {
    setPage(1);
  };

  // Auto-fetch on status / pagination changes only. Dates are intentionally NOT in the
  // deps: a <input type="date"> fires onChange per segment (day, then month, then year),
  // which previously resubmitted the query on every digit. Dates now apply via the
  // quick-date buttons, the month stepper, the Enter key, or the Refresh button.
  useEffect(() => {
    if (startDate && endDate) {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, page, pageSize]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <Clock size={16} color="#f59e0b" />;
      case 'paid':
        return <CheckCircle2 size={16} color="#10b981" />;
      case 'assigned':
        return <CheckCircle2 size={16} color="#059669" />;
      case 'cancelled':
        return <XCircle size={16} color="#ef4444" />;
      case 'failed':
        return <AlertTriangle size={16} color="#ef4444" />;
      default:
        return <Clock size={16} color="#6b7280" />;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fef3c7', text: '#d97706', border: '#fed7aa' };
      case 'paid':
        return { bg: '#dcfce7', text: '#15803d', border: '#bbf7d0' };
      case 'assigned':
        return { bg: '#d1fae5', text: '#059669', border: '#a7f3d0' };
      case 'cancelled':
        return { bg: '#fee2e2', text: '#dc2626', border: '#fecaca' };
      case 'failed':
        return { bg: '#fee2e2', text: '#dc2626', border: '#fecaca' };
      default:
        return { bg: '#f3f4f6', text: '#374151', border: '#d1d5db' };
    }
  };

  const getOrderTypeIcon = (orderType) => {
    switch (orderType) {
      case 'manual_request':
        return <User size={16} color="#6366f1" />;
      case 'payment_pending':
        return <DollarSign size={16} color="#f59e0b" />;
      case 'payment_completed':
        return <CreditCard size={16} color="#10b981" />;
      default:
        return <ShoppingCart size={16} color="#6b7280" />;
    }
  };

  const getOrderTypeLabel = (orderType) => {
    switch (orderType) {
      case 'manual_request':
        return 'Manual Request';
      case 'payment_pending':
        return 'Payment Pending';
      case 'payment_completed':
        return 'Payment Completed';
      default:
        return 'Order';
    }
  };

  const formatPrice = (price, currency = 'INR') => {
    if (currency === 'INR') {
      return `₹${price.toLocaleString('en-IN')}`;
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      const d = new Date(dateString);
      return d.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: displayTz
      });
    } catch {
      return dateString;
    }
  };

  // Summary is now calculated on backend and stored in state
  // No need for frontend calculation

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <div style={{ textAlign: 'center' }}>
          <RefreshCw size={40} color="#667eea" style={{
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading orders...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '40px',
        textAlign: 'center'
      }}>
        <Card style={{ padding: '40px' }}>
          <AlertCircle size={48} color="#ef4444" style={{ margin: '0 auto 16px' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>
            Error Loading Orders
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '20px' }}>{error}</p>
          <Button onClick={() => fetchOrders()} style={{ backgroundColor: '#667eea', color: 'white' }}>
            <RefreshCw size={16} style={{ marginRight: '8px' }} />
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  // Summary is already in state from backend response

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Header with Summary Cards */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px'
        }}>
          <div>
            <h2 style={{
              fontSize: '28px',
              fontWeight: '700',
              color: '#111827',
              margin: '0 0 4px 0'
            }}>
              Order Management
            </h2>
            <p style={{
              fontSize: '16px',
              color: '#6b7280',
              margin: 0
            }}>
              Track and manage plan purchases and requests
            </p>
          </div>

          <Button
            onClick={async () => {
              const result = await handleFixInconsistentOrders();
              if (result) {
                setFixSummary(result.summary || null);
                setShowFixSummaryModal(true);
              }
            }}
            variant="outline"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#fef3c7',
              borderColor: '#fcd34d',
              color: '#92400e'
            }}
          >
            <AlertTriangle size={16} />
            Fix Orders
          </Button>
        </div>

        {/* Filters Row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '20px',
          alignItems: 'center'
        }}>
          {/* Status Filter */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <Filter size={16} color="#6b7280" />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                handleFilterChange();
              }}
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                color: '#374151',
                outline: 'none',
                cursor: 'pointer'
              }}
            >
              <option value="all">All Orders</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
              <option value="assigned">Assigned</option>
              <option value="cancelled">Cancelled</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          {/* Date Range Inputs */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            border: '1px solid #e5e7eb'
          }}>
            <Calendar size={16} color="#6b7280" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchOrders(); }}
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                color: '#374151',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
            <span style={{ color: '#9ca3af' }}>→</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchOrders(); }}
              style={{
                border: 'none',
                backgroundColor: 'transparent',
                fontSize: '14px',
                color: '#374151',
                outline: 'none',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Quick Date Filters */}
          <Button
            onClick={() => setDateFilter('today')}
            size="sm"
            variant="outline"
            style={{ fontSize: '13px' }}
          >
            Today
          </Button>
          <Button
            onClick={() => setDateFilter('last7days')}
            size="sm"
            variant="outline"
            style={{ fontSize: '13px' }}
          >
            Last 7 Days
          </Button>
          <Button
            onClick={() => setDateFilter('thisMonth')}
            size="sm"
            variant="outline"
            style={{ fontSize: '13px' }}
          >
            This Month
          </Button>
          <Button
            onClick={() => setDateFilter('lastMonth')}
            size="sm"
            variant="outline"
            style={{ fontSize: '13px' }}
          >
            Last Month
          </Button>

          {/* Month stepper — relative to the currently-viewed start date */}
          <Button
            onClick={() => stepMonth(-1)}
            size="sm"
            variant="outline"
            style={{ fontSize: '13px' }}
          >
            ← Prev Month
          </Button>
          <Button
            onClick={() => stepMonth(1)}
            size="sm"
            variant="outline"
            style={{ fontSize: '13px' }}
          >
            Next Month →
          </Button>
          <Button
            onClick={() => setDateFilter('ytd')}
            size="sm"
            variant="outline"
            style={{ fontSize: '13px' }}
          >
            Year to Date
          </Button>

          {/* Refresh Button */}
          <Button onClick={() => fetchOrders()} variant="outline" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginLeft: 'auto'
          }}>
            <RefreshCw size={16} />
            Refresh
          </Button>
        </div>

        {/* Results Info and Page Size Selector */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Showing{' '}
            <span style={{ fontWeight: '600', color: '#374151' }}>
              {pagination.total_orders === 0 ? 0 : (page - 1) * pageSize + 1}
            </span>
            {' '}-{' '}
            <span style={{ fontWeight: '600', color: '#374151' }}>
              {Math.min(page * pageSize, pagination.total_orders)}
            </span>
            {' '}of{' '}
            <span style={{ fontWeight: '600', color: '#374151' }}>
              {pagination.total_orders}
            </span>
            {' '}orders
          </div>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>Show:</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1); // Reset to page 1
              }}
              style={{
                padding: '4px 8px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#374151',
                backgroundColor: 'white',
                cursor: 'pointer',
                outline: 'none'
              }}
            >
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option value="200">200</option>
            </select>
            <span style={{ fontSize: '14px', color: '#6b7280' }}>per page</span>
          </div>
        </div>

        {/* Summary Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          <Card style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            border: 'none'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', opacity: 0.9, margin: '0 0 4px 0' }}>Total Orders</p>
                <p style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>{summary.total_orders}</p>
              </div>
              <ShoppingCart size={32} style={{ opacity: 0.8 }} />
            </div>
          </Card>

          <Card style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            color: 'white',
            border: 'none'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', opacity: 0.9, margin: '0 0 4px 0' }}>Revenue</p>
                <p style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>
                  ₹{summary.total_revenue.toLocaleString('en-IN')}
                </p>
              </div>
              <DollarSign size={32} style={{ opacity: 0.8 }} />
            </div>
          </Card>

          <Card style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            color: 'white',
            border: 'none'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', opacity: 0.9, margin: '0 0 4px 0' }}>Pending</p>
                <p style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>{summary.pending}</p>
              </div>
              <Clock size={32} style={{ opacity: 0.8 }} />
            </div>
          </Card>

          <Card style={{
            padding: '20px',
            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
            color: 'white',
            border: 'none'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <p style={{ fontSize: '14px', opacity: 0.9, margin: '0 0 4px 0' }}>Failed</p>
                <p style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>{summary.failed}</p>
              </div>
              <AlertTriangle size={32} style={{ opacity: 0.8 }} />
            </div>
          </Card>
        </div>
      </div>

      {/* Orders List */}
      {orders.length === 0 ? (
        <Card style={{ padding: '60px', textAlign: 'center' }}>
          <ShoppingCart size={64} color="#d1d5db" style={{ margin: '0 auto 24px' }} />
          <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#374151', margin: '0 0 12px 0' }}>
            No Orders Found
          </h3>
          <p style={{ fontSize: '16px', color: '#6b7280', margin: '0 0 24px 0' }}>
            {statusFilter && statusFilter !== 'all' ? `No orders found with status: ${statusFilter}` : 'No orders have been created yet.'}
          </p>
          {statusFilter && statusFilter !== 'all' && (
            <Button
              onClick={() => setStatusFilter('all')}
              variant="outline"
              style={{ marginTop: '16px' }}
            >
              Show All Orders
            </Button>
          )}
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: '20px' }}>
          {orders.map((order) => {
            const statusColors = getStatusBadgeColor(order.status);
            
            return (
              <Card key={order.id} style={{ 
                padding: '24px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                transition: 'all 0.2s ease',
                ':hover': {
                  borderColor: '#d1d5db',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '20px'
                }}>
                  <div style={{ flex: 1 }}>
                    {/* Order Header */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      marginBottom: '12px',
                      flexWrap: 'wrap'
                    }}>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {getOrderTypeIcon(order.order_type)}
                        <span style={{
                          fontSize: '18px',
                          fontWeight: '600',
                          color: '#111827'
                        }}>
                          {order.plan_name}
                        </span>
                      </div>
                      
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        padding: '6px 12px',
                        borderRadius: '20px',
                        fontSize: '13px',
                        fontWeight: '500',
                        backgroundColor: statusColors.bg,
                        color: statusColors.text,
                        border: `1px solid ${statusColors.border}`
                      }}>
                        {getStatusIcon(order.status)}
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </div>

                      <div style={{
                        padding: '4px 8px',
                        backgroundColor: '#f3f4f6',
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: '#6b7280',
                        fontWeight: '500'
                      }}>
                        {getOrderTypeLabel(order.order_type)}
                      </div>
                    </div>

                    {/* User Info */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: '12px'
                    }}>
                      <User size={16} color="#6b7280" />
                      <span style={{
                        fontSize: '14px',
                        color: '#374151',
                        fontWeight: '500'
                      }}>
                        {order.user_name}
                      </span>
                      <span style={{
                        fontSize: '14px',
                        color: '#6b7280'
                      }}>
                        ({order.user_email})
                      </span>
                    </div>

                    {/* Order Details Grid */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                      marginBottom: '16px'
                    }}>
                      {/* Amount */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <DollarSign size={16} color="#10b981" />
                        <div>
                          <span style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#10b981'
                          }}>
                            {formatPrice(order.amount, order.currency)}
                          </span>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            {order.plan_type} plan
                          </div>
                        </div>
                      </div>

                      {/* Credits Granted */}
                      {typeof order.credits_granted !== 'undefined' && order.credits_granted !== null && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Package size={16} color="#6366f1" />
                          <div>
                            <span style={{
                              fontSize: '16px',
                              fontWeight: '700',
                              color: '#4f46e5'
                            }}>
                              {order.credits_granted?.toLocaleString('en-IN')}
                            </span>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280'
                            }}>
                              Credits Granted
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Last Updated */}
                      {order.updated_at && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Calendar size={16} color="#6b7280" />
                          <div>
                            <div style={{
                              fontSize: '14px',
                              color: '#374151',
                              fontWeight: '500'
                            }}>
                              {formatDate(order.updated_at)}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280'
                            }}>
                              Last updated
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Last Reconciled */}
                      {order.last_reconciled_at && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Calendar size={16} color="#6b7280" />
                          <div>
                            <div style={{
                              fontSize: '14px',
                              color: '#374151',
                              fontWeight: '500'
                            }}>
                              {formatDate(order.last_reconciled_at)}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280'
                            }}>
                              Last reconciled
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Date */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Calendar size={16} color="#6b7280" />
                        <div>
                          <div style={{
                            fontSize: '14px',
                            color: '#374151',
                            fontWeight: '500'
                          }}>
                            {formatDate(order.created_at)}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            Order created
                          </div>
                        </div>
                      </div>

                      {/* Razorpay Payment ID */}
                      {order.razorpay_payment_id && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <Receipt size={16} color="#667eea" />
                          <div>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px'
                            }}>
                              <span style={{
                                fontSize: '13px',
                                color: '#374151',
                                fontWeight: '500',
                                fontFamily: 'monospace'
                              }}>
                                {order.razorpay_payment_id}
                              </span>
                              <button
                                onClick={() => copyToClipboard(order.razorpay_payment_id, 'Payment ID')}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  cursor: 'pointer',
                                  padding: '2px',
                                  borderRadius: '4px',
                                  color: '#6b7280'
                                }}
                                title="Copy Payment ID"
                              >
                                <Copy size={12} />
                              </button>
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#6b7280'
                            }}>
                              Razorpay Payment ID
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Order ID */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Hash size={16} color="#6b7280" />
                        <div>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <span style={{
                              fontSize: '12px',
                              color: '#6b7280',
                              fontFamily: 'monospace'
                            }}>
                              {order.id.slice(0, 8)}...
                            </span>
                            <button
                              onClick={() => copyToClipboard(order.id, 'Order ID')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px',
                                borderRadius: '4px',
                                color: '#6b7280'
                              }}
                              title="Copy Order ID"
                            >
                              <Copy size={12} />
                            </button>
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            Order ID
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Failed Payment Details - Enhanced */}
                    {order.status === 'failed' && order.failure_reason && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#fef2f2',
                        borderRadius: '8px',
                        border: '1px solid #fecaca'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          marginBottom: '8px'
                        }}>
                          <AlertTriangle size={16} color="#dc2626" />
                          <span style={{
                            fontSize: '14px',
                            fontWeight: '600',
                            color: '#dc2626'
                          }}>
                            Payment Failed
                          </span>
                        </div>

                        {/* Main Error Message */}
                        <p style={{
                          fontSize: '13px',
                          color: '#7f1d1d',
                          margin: '0 0 12px 0',
                          lineHeight: '1.5'
                        }}>
                          {order.failure_reason}
                        </p>

                        {/* Payment Attempt Details Grid */}
                        {(order.last_payment_error_code || order.last_payment_error_source || order.payment_attempt_count) && (
                          <div style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                            gap: '8px',
                            paddingTop: '12px',
                            borderTop: '1px solid #fecaca'
                          }}>
                            {/* Error Code */}
                            {order.last_payment_error_code && (
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}>
                                <span style={{
                                  fontSize: '11px',
                                  color: '#991b1b',
                                  fontWeight: '600',
                                  textTransform: 'uppercase'
                                }}>
                                  Error Code
                                </span>
                                <span style={{
                                  fontSize: '12px',
                                  color: '#7f1d1d',
                                  fontFamily: 'monospace',
                                  backgroundColor: '#fee2e2',
                                  padding: '4px 6px',
                                  borderRadius: '4px'
                                }}>
                                  {order.last_payment_error_code}
                                </span>
                              </div>
                            )}

                            {/* Error Source */}
                            {order.last_payment_error_source && (
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}>
                                <span style={{
                                  fontSize: '11px',
                                  color: '#991b1b',
                                  fontWeight: '600',
                                  textTransform: 'uppercase'
                                }}>
                                  Error Source
                                </span>
                                <span style={{
                                  fontSize: '12px',
                                  color: '#7f1d1d',
                                  fontWeight: '500'
                                }}>
                                  {order.last_payment_error_source === 'bank' ? '🏦 Bank' :
                                   order.last_payment_error_source === 'customer' ? '👤 Customer' :
                                   order.last_payment_error_source === 'business' ? '🏢 Business' :
                                   order.last_payment_error_source}
                                </span>
                              </div>
                            )}

                            {/* Payment Attempts */}
                            {order.payment_attempt_count > 0 && (
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}>
                                <span style={{
                                  fontSize: '11px',
                                  color: '#991b1b',
                                  fontWeight: '600',
                                  textTransform: 'uppercase'
                                }}>
                                  Attempts
                                </span>
                                <span style={{
                                  fontSize: '12px',
                                  color: '#7f1d1d',
                                  fontWeight: '600'
                                }}>
                                  {order.payment_attempt_count} {order.payment_attempt_count === 1 ? 'attempt' : 'attempts'}
                                </span>
                              </div>
                            )}

                            {/* Last Attempt Time */}
                            {order.last_payment_attempt_at && (
                              <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}>
                                <span style={{
                                  fontSize: '11px',
                                  color: '#991b1b',
                                  fontWeight: '600',
                                  textTransform: 'uppercase'
                                }}>
                                  Last Attempt
                                </span>
                                <span style={{
                                  fontSize: '12px',
                                  color: '#7f1d1d'
                                }}>
                                  {formatDate(order.last_payment_attempt_at)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Admin Notes */}
                    {order.admin_notes && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#374151',
                          marginBottom: '4px'
                        }}>
                          Admin Notes:
                        </div>
                        <p style={{
                          fontSize: '13px',
                          color: '#6b7280',
                          margin: 0
                        }}>
                          {order.admin_notes}
                        </p>
                      </div>
                    )}

                    {/* Assignment Info */}
                    {order.assigned_at && (
                      <div style={{
                        marginTop: '12px',
                        padding: '12px',
                        backgroundColor: '#dcfce7',
                        borderRadius: '8px',
                        border: '1px solid #bbf7d0'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          flexWrap: 'wrap'
                        }}>
                          <CheckCircle2 size={16} color="#059669" />
                          <span style={{
                            fontSize: '13px',
                            color: '#059669',
                            fontWeight: '500'
                          }}>
                            Assigned on {formatDate(order.assigned_at)}
                          </span>
                          {order.assigned_by && (
                            <span style={{
                              fontSize: '11px',
                              padding: '2px 8px',
                              borderRadius: '9999px',
                              backgroundColor: badgeForAssignedBy(order.assigned_by).bg,
                              color: badgeForAssignedBy(order.assigned_by).fg,
                              border: `1px solid ${badgeForAssignedBy(order.assigned_by).border}`
                            }}>
                              {badgeForAssignedBy(order.assigned_by).label}
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginLeft: '16px' }}>
                    <Button
                      onClick={() => {
                        setSelectedOrder(order);
                        setShowDetailsModal(true);
                      }}
                      size="sm"
                      variant="outline"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <Eye size={14} />
                      Details
                    </Button>

                    {/* Sync Status Button - Show for orders with payment gateway integration */}
                    {(order.razorpay_order_id || order.razorpay_payment_link_id ||
                      (order.payment_integration_type && order.status !== 'assigned')) && (
                      <Button
                        onClick={() => handleSyncOrderStatus(order.id)}
                        size="sm"
                        variant="outline"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap',
                          borderColor: '#667eea',
                          color: '#667eea'
                        }}
                        disabled={syncingOrderId === order.id}
                      >
                        {syncingOrderId === order.id ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                            Syncing...
                          </span>
                        ) : (
                          <>
                            <RefreshCw size={14} />
                            Sync Status
                          </>
                        )}
                      </Button>
                    )}

                    {(order.status === 'pending' || order.status === 'paid') && (
                      <Button
                        onClick={() => {
                          setSelectedOrder(order);
                          setShowAssignModal(true);
                        }}
                        size="sm"
                        style={{
                          backgroundColor: '#10b981',
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          whiteSpace: 'nowrap'
                        }}
                        disabled={assigning && assigningOrderId === order.id}
                      >
                        {assigning && assigningOrderId === order.id ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                            Assigning...
                          </span>
                        ) : (
                          <>
                            <Package size={14} />
                            Assign
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Pagination Controls */}
      {orders.length > 0 && pagination.total_pages > 0 && (
        <div style={{
          marginTop: '32px',
          padding: '20px',
          backgroundColor: '#f9fafb',
          borderRadius: '12px',
          border: '1px solid #e5e7eb'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            {/* Page Info */}
            <div style={{ fontSize: '14px', color: '#6b7280' }}>
              Page{' '}
              <span style={{ fontWeight: '600', color: '#374151' }}>
                {pagination.page}
              </span>
              {' '}of{' '}
              <span style={{ fontWeight: '600', color: '#374151' }}>
                {pagination.total_pages}
              </span>
            </div>

            {/* Pagination Buttons */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              {/* Previous Button */}
              <Button
                onClick={() => setPage(page - 1)}
                disabled={!pagination.has_prev || loading}
                variant="outline"
                size="sm"
                style={{
                  opacity: !pagination.has_prev ? 0.5 : 1,
                  cursor: !pagination.has_prev ? 'not-allowed' : 'pointer'
                }}
              >
                ← Previous
              </Button>

              {/* Page Number Buttons */}
              {[...Array(Math.min(5, pagination.total_pages))].map((_, idx) => {
                let pageNum;
                if (pagination.total_pages <= 5) {
                  pageNum = idx + 1;
                } else if (page <= 3) {
                  pageNum = idx + 1;
                } else if (page >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + idx;
                } else {
                  pageNum = page - 2 + idx;
                }

                return (
                  <Button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    size="sm"
                    style={{
                      backgroundColor: page === pageNum ? '#667eea' : 'white',
                      color: page === pageNum ? 'white' : '#374151',
                      border: `1px solid ${page === pageNum ? '#667eea' : '#d1d5db'}`,
                      minWidth: '36px'
                    }}
                  >
                    {pageNum}
                  </Button>
                );
              })}

              {/* Next Button */}
              <Button
                onClick={() => setPage(page + 1)}
                disabled={!pagination.has_next || loading}
                variant="outline"
                size="sm"
                style={{
                  opacity: !pagination.has_next ? 0.5 : 1,
                  cursor: !pagination.has_next ? 'not-allowed' : 'pointer'
                }}
              >
                Next →
              </Button>
            </div>

            {/* Page Jump Input */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <span style={{ fontSize: '14px', color: '#6b7280' }}>Go to:</span>
              <input
                type="number"
                min="1"
                max={pagination.total_pages}
                value={page}
                onChange={(e) => {
                  const val = parseInt(e.target.value);
                  if (val >= 1 && val <= pagination.total_pages) {
                    setPage(val);
                  }
                }}
                style={{
                  width: '60px',
                  padding: '4px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  color: '#374151',
                  textAlign: 'center',
                  outline: 'none'
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Copy Success Notification */}
      {copySuccess && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '12px 16px',
          backgroundColor: '#10b981',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <CheckCircle2 size={16} />
          {copySuccess} copied to clipboard!
        </div>
      )}

      {/* Sync Success Notification */}
      {syncMessage && (
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          padding: '12px 16px',
          backgroundColor: '#667eea',
          color: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          maxWidth: '400px'
        }}>
          <RefreshCw size={16} />
          {syncMessage}
        </div>
      )}

      {/* Order Details Modal */}
      <Modal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedOrder(null);
        }}
        title="Order Details"
        maxWidth="max-w-2xl"
      >
        {selectedOrder && (
          <div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 8px 0'
                }}>
                  Order Information
                </h4>
                <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
                  <p><strong>Order ID:</strong> {selectedOrder.id}</p>
                  <p><strong>Plan:</strong> {selectedOrder.plan_name}</p>
                  <p><strong>Type:</strong> {getOrderTypeLabel(selectedOrder.order_type)}</p>
                  <p><strong>Status:</strong> {selectedOrder.status}</p>
                  <p><strong>Created:</strong> {formatDate(selectedOrder.created_at)}</p>
                </div>
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 8px 0'
                }}>
                  Customer Information
                </h4>
                <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
                  <p><strong>Name:</strong> {selectedOrder.user_name}</p>
                  <p><strong>Email:</strong> {selectedOrder.user_email}</p>
                  <p><strong>User ID:</strong> {selectedOrder.user_id}</p>
                </div>
              </div>

              <div style={{
                padding: '16px',
                backgroundColor: '#f9fafb',
                borderRadius: '8px'
              }}>
                <h4 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  margin: '0 0 8px 0'
                }}>
                  Payment Information
                </h4>
                <div style={{ fontSize: '13px', color: '#6b7280', lineHeight: '1.6' }}>
                  <p><strong>Amount:</strong> {formatPrice(selectedOrder.amount, selectedOrder.currency)}</p>
                  <p><strong>Currency:</strong> {selectedOrder.currency}</p>
                  {selectedOrder.razorpay_order_id && (
                    <p><strong>Razorpay Order ID:</strong> 
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {selectedOrder.razorpay_order_id}
                      </span>
                    </p>
                  )}
                  {selectedOrder.razorpay_payment_id && (
                    <p><strong>Razorpay Payment ID:</strong> 
                      <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
                        {selectedOrder.razorpay_payment_id}
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {selectedOrder.assigned_at && (
                <div style={{
                  padding: '16px',
                  backgroundColor: '#dcfce7',
                  borderRadius: '8px'
                }}>
                  <h4 style={{
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#059669',
                    margin: '0 0 8px 0'
                  }}>
                    Assignment Information
                  </h4>
                  <div style={{ fontSize: '13px', color: '#065f46', lineHeight: '1.6' }}>
                    <p><strong>Assigned by:</strong> {selectedOrder.assigned_by}</p>
                    <p><strong>Assigned on:</strong> {formatDate(selectedOrder.assigned_at)}</p>
                  </div>
                </div>
              )}
            </div>

            {(selectedOrder.admin_notes || selectedOrder.failure_reason) && (
              <div style={{ marginBottom: '20px' }}>
                {selectedOrder.failure_reason && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef2f2',
                    borderRadius: '8px',
                    border: '1px solid #fecaca',
                    marginBottom: '12px'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#dc2626',
                      margin: '0 0 8px 0',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <AlertTriangle size={16} />
                      Payment Failure Details
                    </h4>
                    <p style={{ fontSize: '13px', color: '#7f1d1d', margin: '0 0 12px 0', lineHeight: '1.5' }}>
                      {selectedOrder.failure_reason}
                    </p>

                    {/* Additional Error Details */}
                    {(selectedOrder.last_payment_error_code || selectedOrder.last_payment_error_source ||
                      selectedOrder.last_payment_error_reason || selectedOrder.payment_attempt_count > 0) && (
                      <div style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '10px',
                        marginTop: '12px',
                        paddingTop: '12px',
                        borderTop: '1px solid #fecaca'
                      }}>
                        {selectedOrder.last_payment_error_code && (
                          <div>
                            <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', marginBottom: '4px' }}>
                              ERROR CODE
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#7f1d1d',
                              fontFamily: 'monospace',
                              backgroundColor: '#fee2e2',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              display: 'inline-block'
                            }}>
                              {selectedOrder.last_payment_error_code}
                            </div>
                          </div>
                        )}

                        {selectedOrder.last_payment_error_source && (
                          <div>
                            <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', marginBottom: '4px' }}>
                              ERROR SOURCE
                            </div>
                            <div style={{ fontSize: '12px', color: '#7f1d1d', fontWeight: '500' }}>
                              {selectedOrder.last_payment_error_source === 'bank' && '🏦 Bank'}
                              {selectedOrder.last_payment_error_source === 'customer' && '👤 Customer'}
                              {selectedOrder.last_payment_error_source === 'business' && '🏢 Business'}
                              {!['bank', 'customer', 'business'].includes(selectedOrder.last_payment_error_source) &&
                                selectedOrder.last_payment_error_source}
                            </div>
                          </div>
                        )}

                        {selectedOrder.last_payment_error_reason && (
                          <div>
                            <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', marginBottom: '4px' }}>
                              ERROR REASON
                            </div>
                            <div style={{ fontSize: '12px', color: '#7f1d1d' }}>
                              {selectedOrder.last_payment_error_reason}
                            </div>
                          </div>
                        )}

                        {selectedOrder.payment_attempt_count > 0 && (
                          <div>
                            <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', marginBottom: '4px' }}>
                              PAYMENT ATTEMPTS
                            </div>
                            <div style={{ fontSize: '12px', color: '#7f1d1d', fontWeight: '600' }}>
                              {selectedOrder.payment_attempt_count} {selectedOrder.payment_attempt_count === 1 ? 'attempt' : 'attempts'}
                            </div>
                          </div>
                        )}

                        {selectedOrder.last_payment_attempt_at && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: '11px', color: '#991b1b', fontWeight: '600', marginBottom: '4px' }}>
                              LAST PAYMENT ATTEMPT
                            </div>
                            <div style={{ fontSize: '12px', color: '#7f1d1d' }}>
                              {formatDate(selectedOrder.last_payment_attempt_at)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                
                {selectedOrder.admin_notes && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 8px 0'
                    }}>
      {/* Fix Orders Summary Modal */}
      <Modal
        isOpen={showFixSummaryModal}
        onClose={() => {
          setShowFixSummaryModal(false);
          setFixSummary(null);
        }}
        title="Fix Orders Summary"
        maxWidth="max-w-md"
      >
        {fixSummary ? (
          <div style={{ padding: '8px 0' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Scanned</div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{fixSummary.scanned || 0}</div>
              </div>
              <div style={{ background: '#ecfdf5', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px' }}>
                <div style={{ fontSize: '12px', color: '#065f46' }}>Marked Paid</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#065f46' }}>{fixSummary.marked_paid || 0}</div>
              </div>
              <div style={{ background: '#dcfce7', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '10px' }}>
                <div style={{ fontSize: '12px', color: '#065f46' }}>Assigned</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#065f46' }}>{fixSummary.assigned || 0}</div>
              </div>
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '10px' }}>
                <div style={{ fontSize: '12px', color: '#7f1d1d' }}>Skipped (Already Assigned)</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#7f1d1d' }}>{fixSummary.skipped_already_assigned || 0}</div>
              </div>
              <div style={{ background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px' }}>
                <div style={{ fontSize: '12px', color: '#374151' }}>Unchanged</div>
                <div style={{ fontSize: '18px', fontWeight: 700 }}>{fixSummary.unchanged || 0}</div>
              </div>
              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', padding: '10px' }}>
                <div style={{ fontSize: '12px', color: '#9a3412' }}>Errors</div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: '#9a3412' }}>{(fixSummary.errors || []).length}</div>
              </div>
            </div>

            {(fixSummary.errors || []).length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <div style={{ fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Errors</div>
                <div style={{
                  maxHeight: '200px',
                  overflowY: 'auto',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '8px',
                  background: '#fff'
                }}>
                  {(fixSummary.errors || []).map((e, idx) => (
                    <div key={idx} style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                      <strong>Order:</strong> {e.order_id} — {e.reason}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
              <Button onClick={() => { setShowFixSummaryModal(false); setFixSummary(null); }} variant="outline">Close</Button>
            </div>
          </div>
        ) : (
          <div style={{ padding: '12px', color: '#6b7280' }}>No summary.</div>
        )}
      </Modal>

                      Admin Notes
                    </h4>
                    <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                      {selectedOrder.admin_notes}
                    </p>
                  </div>
                )}
              </div>
            )}

            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px'
            }}>
              <Button
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedOrder(null);
                }}
                variant="outline"
              >
                Close
              </Button>
              
              {(selectedOrder.status === 'pending' || selectedOrder.status === 'paid') && (
                <Button
                  onClick={() => {
                    setShowDetailsModal(false);
                    setShowAssignModal(true);
                  }}
                  style={{
                    backgroundColor: '#10b981',
                    color: 'white'
                  }}
                >
                  Assign Plan
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Assign Plan Confirmation Modal */}
      <Modal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setSelectedOrder(null);
        }}
        title="Assign Plan"
        maxWidth="max-w-md"
      >
        {selectedOrder && (
          <div>
            <div style={{
              padding: '20px',
              backgroundColor: '#f9fafb',
              borderRadius: '12px',
              marginBottom: '24px'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: '600',
                color: '#111827',
                margin: '0 0 12px 0'
              }}>
                Order Summary
              </h4>
              <div style={{ fontSize: '14px', color: '#6b7280', lineHeight: '1.6' }}>
                <p><strong>Plan:</strong> {selectedOrder.plan_name}</p>
                <p><strong>Customer:</strong> {selectedOrder.user_name}</p>
                <p><strong>Email:</strong> {selectedOrder.user_email}</p>
                <p><strong>Amount:</strong> {formatPrice(selectedOrder.amount, selectedOrder.currency)}</p>
                <p><strong>Status:</strong> {selectedOrder.status}</p>
              </div>
            </div>

            <div style={{
              padding: '16px',
              backgroundColor: '#fffbeb',
              borderRadius: '8px',
              border: '1px solid #fcd34d',
              marginBottom: '24px'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: '#92400e',
                margin: '0 0 8px 0'
              }}>
                ⚠️ Assignment Actions
              </h4>
              <ul style={{
                fontSize: '13px',
                color: '#78350f',
                margin: 0,
                paddingLeft: '20px',
                lineHeight: '1.6'
              }}>
                <li>Cancel any existing active subscription</li>
                <li>Create a new subscription with the selected plan</li>
                <li>Grant credits according to the plan</li>
                <li>Mark this order as "assigned"</li>
                <li>Send confirmation email to user</li>
              </ul>
            </div>

            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <Button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedOrder(null);
                }}
                variant="outline"
                disabled={assigning}
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleAssignOrder(selectedOrder.id)}
                disabled={assigning}
                style={{
                  backgroundColor: '#10b981',
                  color: 'white'
                }}
              >
                {assigning ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <RefreshCw size={14} style={{ animation: 'spin 1s linear infinite' }} />
                    Assigning...
                  </span>
                ) : (
                  'Confirm Assignment'
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default OrderManagementTab;